import type { ILogger, LoggerOptions } from '@/utils/logger'
import axios from 'axios'
import ErrorStackParser, { type StackFrame } from 'error-stack-parser'
import sourceMap from 'source-map-js'
import {
    getCurrentInstance,
    inject,
    type InjectionKey,
    type Plugin,
    readonly,
} from 'vue'
import { useGlobalStore } from '@/stores/globalStore'
import { LoggerFactory } from '@/utils/logger'

const LOGGER_INJECTION_KEY: InjectionKey<ILogger> = Symbol('logger')
let activeLogger: ILogger

interface UseLoggerOptions extends LoggerOptions {
    captureComponentErrors?: boolean
}

const LoadSourceMap = (url: string) => {
    const sourceMapUrl = url.replace('assets', 'sourceMap')

    return axios.get(sourceMapUrl)
}

const printStackFrame = async (stackFrames: StackFrame[]) => {
    const stackTraceList: string[] = []
    const sourceMapUrl = `${stackFrames[0].fileName}.map`

    const sourceData = await LoadSourceMap(sourceMapUrl)
    const fileContent = sourceData.data
    const consumer = new sourceMap.SourceMapConsumer(fileContent)
    for (const stackFrame of stackFrames) {
        const lookUpResult = consumer.originalPositionFor({
            line: stackFrame.lineNumber ?? 0,
            column: stackFrame.columnNumber ?? 0,
        })
        stackTraceList.push(`\n at ${lookUpResult.source} <${lookUpResult.line}:${lookUpResult.column}>`)
    }

    return stackTraceList
}
async function getErrorHandler(logger: ILogger) {
    return (error: unknown) => {
        const tempError = error as Record<string, unknown>

        if (import.meta.env.PROD || import.meta.env.MODE === 'staging') {
            const stackFrame = ErrorStackParser.parse(error as Error)
            printStackFrame(stackFrame).then((mappedPosition) => {
                if (mappedPosition) {
                    tempError.tempExceptionStack = mappedPosition.join('')
                }
            })
        }

        tempError.loginName = useGlobalStore().appProps.loginName

        logger.logError('COMPONENT_ERROR', tempError)
    }
}

export function createLogger(options: UseLoggerOptions): Plugin & { logger: ILogger } {
    const logger = readonly(LoggerFactory.createLogger(options))
    const { captureComponentErrors = true } = options
    activeLogger = logger

    return {
        async install(app) {
            app.provide(LOGGER_INJECTION_KEY, logger)
            if (captureComponentErrors) {
                app.config.errorHandler = await getErrorHandler(logger)
            }
        },
        logger,
    }
}

export function useLogger(): ILogger {
    const logger = (getCurrentInstance() && inject(LOGGER_INJECTION_KEY)) || activeLogger
    if (!logger) {
        throw new Error('logger instance not found')
    }

    return logger
}
